/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-nested-ternary */

/**
 * #######################################################@
 *
 * Compact date filter, made for <FiltersPanel />
 *
 * Consistent with <CompactSelectFilter />: a select-like
 * trigger showing the current range, which opens its OWN
 * popover (rendered at body level, so the calendar floats
 * above the filters panel instead of overflowing inside it).
 *
 * The popover holds quick presets + two INDEPENDENT bounds
 * (start / end): the user can set only a start, only an end,
 * or both — it is not necessarily a continuous range. The
 * calendar (react-day-picker, Airbnb-like styling) edits the
 * dates; a time field per bound edits the hours.
 *
 * The navbar <DateFilter /> is left untouched.
 *
 * #######################################################@
 */
import "react-day-picker/style.css";
import { useEffect, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import { enUS, fr } from "react-day-picker/locale";
import {
	ClickAwayListener,
	Divider,
	Grow,
	Icon,
	IconButton,
	Popover,
	Popper,
	Tooltip
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import FilterTrigger from "components/Custom/Filters/FilterTrigger";
import { getInterval } from "components/Custom/DatePresets";
import { useMaterialUIController } from "context";
import i18n from "i18n";
import { useDispatch } from "react-redux";
import { setFilter } from "redux-react/reducers/filtersReducers";

const LOCALES = { fr, en: enUS };

const PRESETS = [
	{ code: "wtd", key: "FILTERS.date.PRESETS.wtd" },
	{ code: "mtd", key: "FILTERS.date.PRESETS.mtd" },
	{ code: "qtd", key: "FILTERS.date.PRESETS.qtd" },
	{ code: "ytd", key: "FILTERS.date.PRESETS.ytd" },
	{ separator: true },
	{ code: "lastWeek", key: "FILTERS.date.PRESETS.lastWeek" },
	{ code: "lastMonth", key: "FILTERS.date.PRESETS.lastMonth" },
	{ code: "lastQuarter", key: "FILTERS.date.PRESETS.lastQuarter" },
	{ separator: true },
	{ code: "lifestime", key: "FILTERS.date.PRESETS.lifestime" }
];

// The reduced panel is a bit wider than the calendar's natural width
// (7 columns × 36px day cells) so the start / end bound fields can show
// their full "date + time" value without cramping; the calendar stays
// centered within it.
const REDUCED_WIDTH = 400;

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const pad2 = n => String(n).padStart(2, "0");

/* "HH:mm" of a bound, shown in the time field trigger */
const toTimeValue = date => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

/**
 * One scrollable wheel-like column (hours or minutes) for the custom
 * time picker. Auto-scrolls the selected value into view on mount
 * (the popover mounts its content fresh each time it opens).
 */
function TimeColumn({ label, values, selected, onSelect, darkMode }) {
	const selectedRef = useRef(null);

	useEffect(() => {
		selectedRef.current?.scrollIntoView({ block: "center" });
	}, []);

	return (
		<MDBox display="flex" flexDirection="column" minWidth={0} flex={1}>
			<MDTypography
				variant="caption"
				fontWeight="bold"
				color="secondary"
				textAlign="center"
				sx={{ py: 0.5, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.6rem" }}
			>
				{label}
			</MDTypography>
			<MDBox
				sx={{
					height: 168,
					overflowY: "auto",
					px: 0.5,
					// Slim, discreet scrollbar
					scrollbarWidth: "thin",
					"&::-webkit-scrollbar": { width: 4 },
					"&::-webkit-scrollbar-thumb": {
						borderRadius: 4,
						backgroundColor: darkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
					}
				}}
			>
				{values.map(v => {
					const isSel = v === selected;
					return (
						<MDBox
							key={v}
							ref={isSel ? selectedRef : null}
							onClick={() => onSelect(v)}
							sx={({ palette, borders }) => ({
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: 30,
								mb: 0.25,
								borderRadius: borders.borderRadius.md,
								cursor: "pointer",
								fontSize: "0.8rem",
								fontWeight: isSel ? 600 : 400,
								color: isSel
									? palette.white.main
									: darkMode
										? palette.white.main
										: palette.dark.main,
								backgroundColor: isSel ? palette.info.main : "transparent",
								transition: "background-color 0.1s ease",
								"&:hover": {
									backgroundColor: isSel
										? palette.info.main
										: darkMode
											? palette.grey[700]
											: palette.grey[200]
								}
							})}
						>
							{pad2(v)}
						</MDBox>
					);
				})}
			</MDBox>
		</MDBox>
	);
}

/**
 * Time trigger for one bound — a clean read-only field that opens the
 * custom time picker popover. Module-level (stable identity) so its DOM
 * node survives re-renders and stays a valid popover anchor.
 */
function TimeField({ field, value, isOpen, darkMode, onOpen }) {
	return (
		<MDBox
			onClick={e => value && onOpen(field, e.currentTarget)}
			sx={({ palette, borders }) => ({
				flex: 1,
				minWidth: 0,
				display: "flex",
				alignItems: "center",
				gap: 0.75,
				px: 1,
				py: 0.5,
				borderRadius: borders.borderRadius.md,
				border: `1px solid ${
					isOpen ? palette.info.main : darkMode ? palette.grey[700] : palette.grey[300]
				}`,
				backgroundColor: isOpen
					? `${palette.info.main}0d`
					: darkMode
						? palette.background.card
						: palette.white.main,
				opacity: value ? 1 : 0.5,
				cursor: value ? "pointer" : "not-allowed",
				userSelect: "none",
				transition: "border-color 0.1s ease, background-color 0.1s ease"
			})}
		>
			<Icon fontSize="small" sx={{ color: isOpen ? "info.main" : "text.secondary" }}>
				schedule
			</Icon>
			<MDTypography
				variant="button"
				fontWeight="regular"
				color={value ? "text" : "secondary"}
				sx={{ flex: 1 }}
			>
				{value ? toTimeValue(value) : "--:--"}
			</MDTypography>
			<Icon fontSize="small" sx={{ color: "text.secondary" }}>
				{isOpen ? "arrow_drop_up" : "arrow_drop_down"}
			</Icon>
		</MDBox>
	);
}

const isSameDay = (a, b) => a && b && new Date(a).toDateString() === new Date(b).toDateString();

const startOfDay = date => {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d;
};

/**
 * The calendar only picks a DAY: re-apply the time of the bound being
 * edited (or its default — 00:00 start / 23:59 end — when still empty).
 */
const applyTime = (day, ref, defaultHour, defaultMinute) => {
	const d = new Date(day);
	if (ref) d.setHours(ref.getHours(), ref.getMinutes(), 0, 0);
	else d.setHours(defaultHour, defaultMinute, 0, 0);
	return d;
};

export default function CompactDateFilter({ assistantID, page, storage, attribute }) {
	const locale = LOCALES[i18n.language] || LOCALES.en;

	const dispatch = useDispatch();
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;

	const [startDate, setStartDate] = useState(storage?.$gte ? new Date(storage.$gte) : null);
	const [endDate, setEndDate] = useState(storage?.$lte ? new Date(storage.$lte) : null);
	const [activeField, setActiveField] = useState("start");
	const [anchorEl, setAnchorEl] = useState(null);
	// "large" (default): two side-by-side calendars, one per bound.
	// "reduced": a single calendar editing both bounds.
	const [mode, setMode] = useState("reduced");

	// Displayed months are controlled so presets / bound clicks can
	// navigate the calendars to the relevant date.
	const [singleMonth, setSingleMonth] = useState(() => startDate || endDate || new Date());
	const [startMonth, setStartMonth] = useState(() => startDate || new Date());
	const [endMonth, setEndMonth] = useState(() => endDate || new Date());

	// Custom time picker popover: which bound's time is being edited.
	const [timeAnchorEl, setTimeAnchorEl] = useState(null);
	const [timeField, setTimeField] = useState(null);

	const open = Boolean(anchorEl);
	const hasValue = Boolean(startDate || endDate);

	const dispatchFilter = ({ start, end, code = null, mode = "manual" }) => {
		const datePack = {};
		if (start) datePack.$gte = start;
		if (end) datePack.$lte = end;

		dispatch(
			setFilter({
				version: "2.0",
				assistantID,
				page,
				attribute,
				type: "datetime",
				mode,
				code,
				values: datePack
			})
		);
	};

	const commit = (start, end, { code = null, mode = "manual" } = {}) => {
		setStartDate(start);
		setEndDate(end);
		dispatchFilter({ start, end, code, mode });
	};

	const syncMonths = (start, end) => {
		setSingleMonth(start || end || new Date());
		if (start) setStartMonth(start);
		if (end) setEndMonth(end);
	};

	/**
	 * Reduced mode: a single calendar edits both bounds. Outside the
	 * current range → push the bound it overshoots. Otherwise fill the
	 * active field's bound if it is empty, then any empty bound, and
	 * finally snap to the nearest one.
	 */
	const onCalendarPick = day => {
		const dayTime = startOfDay(day).getTime();
		const startDay = startDate ? startOfDay(startDate).getTime() : null;
		const endDay = endDate ? startOfDay(endDate).getTime() : null;

		let target;
		if (startDay !== null && dayTime < startDay) target = "start";
		else if (endDay !== null && dayTime > endDay) target = "end";
		else if (activeField === "end" && !endDate) target = "end";
		else if (activeField === "start" && !startDate) target = "start";
		else if (!startDate) target = "start";
		else if (!endDate) target = "end";
		else target = Math.abs(dayTime - endDay) < Math.abs(dayTime - startDay) ? "end" : "start";

		if (target === "start") {
			commit(applyTime(day, startDate, 0, 0), endDate);
			if (!endDate) setActiveField("end");
		} else {
			commit(startDate, applyTime(day, endDate, 23, 59));
			if (!startDate) setActiveField("start");
		}
	};

	// Large mode: each calendar owns its bound. Picking a start after the
	// current end (or an end before the current start) breaks the range,
	// so we drop the now-inconsistent opposite bound.
	const onPickStart = day => {
		const picked = applyTime(day, startDate, 0, 0);
		const nextEnd = endDate && picked > endDate ? null : endDate;
		commit(picked, nextEnd);
	};

	const onPickEnd = day => {
		const picked = applyTime(day, endDate, 23, 59);
		const nextStart = startDate && picked < startDate ? null : startDate;
		commit(nextStart, picked);
	};

	// Custom time picker: set the hour or minute of the given bound.
	const setBoundTime = (field, { hour, minute }) => {
		const bound = field === "start" ? startDate : endDate;
		if (!bound) return;
		const d = new Date(bound);
		d.setHours(hour ?? d.getHours(), minute ?? d.getMinutes(), 0, 0);
		if (field === "start") commit(d, endDate);
		else commit(startDate, d);
	};

	const closeTimePicker = () => setTimeAnchorEl(null);

	// Click on a time field toggles its dropdown (and moves it when
	// switching from one bound to the other while already open).
	const toggleTimePicker = (field, target) => {
		if (timeAnchorEl && timeField === field) {
			closeTimePicker();
		} else {
			setTimeField(field);
			setTimeAnchorEl(target);
		}
	};

	// The trigger is outside the dropdown, so a click on it also reaches
	// the click-away listener — ignore it there (the toggle handles it).
	const handleTimeClickAway = event => {
		if (timeAnchorEl && timeAnchorEl.contains(event.target)) return;
		closeTimePicker();
	};

	const timeOpen = Boolean(timeAnchorEl);
	const timeBound = timeField === "start" ? startDate : endDate;

	const onPreset = code => {
		const interval = getInterval(code);
		const start = interval.$gte || null;
		const end = interval.$lte || null;
		commit(start, end, { code, mode: "auto" });
		syncMonths(start, end);
	};

	const clearStart = () => commit(null, endDate);
	const clearEnd = () => commit(startDate, null);
	const clearAll = () => commit(null, null);

	const formatDate = date => {
		try {
			return new Date(date).toLocaleDateString(i18n.language);
		} catch (e) {
			return "";
		}
	};

	const formatDateTime = date => {
		try {
			const d = new Date(date);
			const time = d.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });
			return `${d.toLocaleDateString(i18n.language)} ${time}`;
		} catch (e) {
			return "";
		}
	};

	const summary = (() => {
		const start = startDate ? formatDate(startDate) : null;
		const end = endDate ? formatDate(endDate) : null;
		if (start && end) return i18n.t("FILTERS.panel.fromTo", { start, end });
		if (start) return i18n.t("FILTERS.panel.from", { start });
		if (end) return i18n.t("FILTERS.panel.to", { end });
		return "";
	})();

	/**
	 * Day markers. `bandRight` / `bandLeft` paint the half-width range
	 * band behind the start / end circles — only when a real (multi-day)
	 * range exists.
	 */
	const hasBand = Boolean(startDate && endDate && !isSameDay(startDate, endDate));

	const rangeModifiers = {
		rangeStart: startDate || [],
		rangeEnd: endDate || [],
		rangeBetween: day => {
			if (!startDate || !endDate) return false;
			const t = startOfDay(day).getTime();
			return t > startOfDay(startDate).getTime() && t < startOfDay(endDate).getTime();
		},
		bandRight: hasBand ? startDate : [],
		bandLeft: hasBand ? endDate : []
	};

	const MODIFIER_CLASSNAMES = {
		rangeStart: "cdf-start",
		rangeEnd: "cdf-end",
		rangeBetween: "cdf-between",
		bandRight: "cdf-band-right",
		bandLeft: "cdf-band-left"
	};

	/**
	 * Airbnb-like calendar skin: circled days, filled circles on the
	 * bounds, soft band across the in-between span, ring on hover.
	 */
	const calendarSx =
		(daySize = 36) =>
		({ palette, functions }) => {
			const accent = palette.info.main;
			const band = darkMode ? functions.rgba(palette.info.main, 0.16) : `${palette.info.main}14`;
			const textColor = darkMode ? palette.white.main : palette.dark.main;
			const mutedColor = darkMode ? palette.grey[500] : palette.grey[600];
			const hoverRing = darkMode ? palette.grey[400] : palette.dark.main;
			const px = `${daySize}px`;

			return {
				display: "flex",
				justifyContent: "center",
				"& .rdp-root": {
					"--rdp-accent-color": accent,
					"--rdp-accent-background-color": band,
					"--rdp-day-width": px,
					"--rdp-day-height": px,
					"--rdp-day_button-width": px,
					"--rdp-day_button-height": px,
					"--rdp-nav_button-width": "28px",
					"--rdp-nav_button-height": "28px",
					"--rdp-today-color": accent,
					"--rdp-weekday-padding": "0.4em 0",
					margin: 0,
					padding: 0,
					fontSize: "0.8rem",
					color: textColor
				},
				"& .rdp-months": {
					justifyContent: "center"
				},
				"& .rdp-month_grid": {
					borderCollapse: "separate",
					borderSpacing: "0 2px"
				},
				// Centered month label, chevrons on both sides (Airbnb layout)
				"& .rdp-month_caption": {
					justifyContent: "center",
					height: "var(--rdp-nav-height)",
					fontSize: "0.85rem",
					fontWeight: 600,
					textTransform: "capitalize"
				},
				"& .rdp-nav": {
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					height: "var(--rdp-nav-height)",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center"
				},
				"& .rdp-button_previous, & .rdp-button_next": {
					borderRadius: "50%",
					"&:hover": {
						backgroundColor: darkMode ? palette.grey[700] : palette.grey[200]
					}
				},
				"& .rdp-chevron": {
					fill: textColor,
					width: "14px",
					height: "14px"
				},
				"& .rdp-weekday": {
					fontSize: "0.7rem",
					fontWeight: 600,
					color: mutedColor,
					textTransform: "capitalize",
					opacity: 1
				},
				"& .rdp-day_button": {
					borderRadius: "50%",
					fontWeight: 500,
					transition: "box-shadow 0.1s ease, background-color 0.1s ease",
					"&:hover:not([disabled])": {
						boxShadow: `inset 0 0 0 1.5px ${hoverRing}`
					}
				},
				"& .rdp-today:not(.cdf-start):not(.cdf-end) .rdp-day_button": {
					fontWeight: 700,
					color: accent
				},
				// Bounds: filled circles
				"& .cdf-start .rdp-day_button, & .cdf-end .rdp-day_button": {
					backgroundColor: accent,
					color: palette.white.main,
					fontWeight: 600,
					"&:hover:not([disabled])": {
						boxShadow: "none"
					}
				},
				// In-between span: soft band behind the circles
				"& .cdf-between": {
					backgroundColor: band
				},
				"& .cdf-band-right": {
					background: `linear-gradient(to right, transparent 50%, ${band} 50%)`
				},
				"& .cdf-band-left": {
					background: `linear-gradient(to left, transparent 50%, ${band} 50%)`
				},
				"& .rdp-outside": {
					opacity: 0.4
				}
			};
		};

	const renderCalendar = ({ month, onMonthChange, onDayClick, modifiers, daySize }) => (
		<MDBox sx={calendarSx(daySize)}>
			<DayPicker
				locale={locale}
				weekStartsOn={1}
				month={month}
				onMonthChange={onMonthChange}
				onDayClick={onDayClick}
				modifiers={modifiers}
				modifiersClassNames={MODIFIER_CLASSNAMES}
				showOutsideDays
			/>
		</MDBox>
	);

	/**
	 * One bound field (start / end)
	 */
	const BoundField = ({ field, label, value, onClear }) => {
		const isActive = activeField === field;
		return (
			<MDBox
				onClick={() => {
					setActiveField(field);
					if (value) setSingleMonth(value);
				}}
				sx={({ palette, borders }) => ({
					flex: 1,
					minWidth: 0,
					display: "flex",
					flexDirection: "column",
					px: 1,
					py: 0.5,
					cursor: "pointer",
					borderRadius: borders.borderRadius.md,
					border: `1px solid ${
						isActive ? palette.info.main : darkMode ? palette.grey[700] : palette.grey[300]
					}`,
					backgroundColor: isActive
						? `${palette.info.main}0d`
						: darkMode
							? palette.background.card
							: palette.white.main
				})}
			>
				<MDTypography
					variant="caption"
					fontWeight="bold"
					color={isActive ? "info" : "secondary"}
					sx={{ lineHeight: 1.2 }}
				>
					{label}
				</MDTypography>
				<MDBox display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
					<MDTypography
						variant="button"
						fontWeight="regular"
						color={value ? "text" : "secondary"}
						noWrap
						sx={{ display: "block", minWidth: 0 }}
					>
						{value ? formatDateTime(value) : i18n.t("FILTERS.panel.selectPlaceholder")}
					</MDTypography>
					{value && (
						<IconButton
							size="small"
							sx={{ p: 0.25 }}
							onClick={e => {
								e.stopPropagation();
								onClear();
							}}
						>
							<Icon fontSize="small" sx={{ color: "text.secondary" }}>
								close
							</Icon>
						</IconButton>
					)}
				</MDBox>
			</MDBox>
		);
	};

	return (
		<>
			<FilterTrigger
				icon="calendar_today"
				open={open}
				active={hasValue}
				text={summary}
				placeholder={i18n.t("FILTERS.panel.selectPlaceholder")}
				onClick={e => setAnchorEl(e.currentTarget)}
				onClear={clearAll}
				darkMode={darkMode}
			/>

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				slotProps={{
					paper: {
						sx: ({ palette, borders, boxShadows }) => ({
							mt: 0.5,
							display: "flex",
							flexDirection: "row",
							borderRadius: borders.borderRadius.lg,
							border: `1px solid ${darkMode ? palette.grey[700] : palette.grey[300]}`,
							backgroundColor: darkMode ? palette.background.card : palette.white.main,
							boxShadow: boxShadows.lg,
							overflow: "hidden"
						})
					}
				}}
			>
				{/* Quick presets */}
				<MDBox
					sx={({ palette }) => ({
						minWidth: 150,
						py: 0.75,
						borderRight: `1px solid ${darkMode ? palette.grey[700] : palette.grey[300]}`,
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between"
					})}
				>
					{PRESETS.map((preset, key) =>
						preset.separator ? (
							<Divider key={key} sx={{ my: 0.5 }} />
						) : (
							<MDBox
								key={preset.code}
								onClick={() => onPreset(preset.code)}
								sx={({ palette, borders }) => ({
									mx: 0.75,
									px: 1,
									py: 0.5,
									borderRadius: borders.borderRadius.md,
									cursor: "pointer",
									"&:hover": {
										backgroundColor: darkMode ? palette.grey[700] : palette.grey[200]
									}
								})}
							>
								<MDTypography variant="button" fontWeight="regular" color="text">
									{i18n.t(preset.key)}
								</MDTypography>
							</MDBox>
						)
					)}
				</MDBox>

				{/* Bounds + calendar(s) */}
				<MDBox p={1} display="flex" flexDirection="column" gap={1}>
					{/* Top bar: mode toggle in the top-right corner */}
					<MDBox display="flex" justifyContent="flex-end">
						<Tooltip
							placement="top"
							title={
								mode === "large"
									? i18n.t("FILTERS.panel.reduceView")
									: i18n.t("FILTERS.panel.enlargeView")
							}
						>
							<IconButton
								size="small"
								sx={{ p: 0.25 }}
								onClick={() => setMode(mode === "large" ? "reduced" : "large")}
							>
								<Icon fontSize="small" sx={{ color: "text.secondary" }}>
									{mode === "large" ? "close_fullscreen" : "open_in_full"}
								</Icon>
							</IconButton>
						</Tooltip>
					</MDBox>

					{mode === "large" ? (
						<MDBox display="flex" gap={1.5}>
							{/* Start */}
							<MDBox display="flex" flexDirection="column" gap={0.75}>
								<BoundField
									field="start"
									label={i18n.t("FILTERS.date.start")}
									value={startDate}
									onClear={clearStart}
								/>
								{renderCalendar({
									month: startMonth,
									onMonthChange: setStartMonth,
									onDayClick: onPickStart,
									modifiers: { rangeStart: startDate || [] }
								})}
								<TimeField
									field="start"
									value={startDate}
									isOpen={timeOpen && timeField === "start"}
									darkMode={darkMode}
									onOpen={toggleTimePicker}
								/>
							</MDBox>
							{/* End */}
							<MDBox display="flex" flexDirection="column" gap={0.75}>
								<BoundField
									field="end"
									label={i18n.t("FILTERS.date.end")}
									value={endDate}
									onClear={clearEnd}
								/>
								{renderCalendar({
									month: endMonth,
									onMonthChange: setEndMonth,
									onDayClick: onPickEnd,
									modifiers: { rangeEnd: endDate || [] }
								})}
								<TimeField
									field="end"
									value={endDate}
									isOpen={timeOpen && timeField === "end"}
									darkMode={darkMode}
									onOpen={toggleTimePicker}
								/>
							</MDBox>
						</MDBox>
					) : (
						<MDBox display="flex" flexDirection="column" gap={1} sx={{ width: REDUCED_WIDTH }}>
							<MDBox display="flex" gap={0.75}>
								<BoundField
									field="start"
									label={i18n.t("FILTERS.date.start")}
									value={startDate}
									onClear={clearStart}
								/>
								<BoundField
									field="end"
									label={i18n.t("FILTERS.date.end")}
									value={endDate}
									onClear={clearEnd}
								/>
							</MDBox>

							{renderCalendar({
								month: singleMonth,
								onMonthChange: setSingleMonth,
								onDayClick: onCalendarPick,
								modifiers: rangeModifiers,
								daySize: 48
							})}

							<MDBox display="flex" gap={0.75}>
								<TimeField
									field="start"
									value={startDate}
									isOpen={timeOpen && timeField === "start"}
									darkMode={darkMode}
									onOpen={toggleTimePicker}
								/>
								<TimeField
									field="end"
									value={endDate}
									isOpen={timeOpen && timeField === "end"}
									darkMode={darkMode}
									onOpen={toggleTimePicker}
								/>
							</MDBox>
						</MDBox>
					)}
				</MDBox>
			</Popover>

			{/*
			 * Custom time picker — two scrollable columns (hours / minutes).
			 * A Popper (not a Popover): no backdrop / modal, so it floats
			 * cleanly above the date popover instead of fighting its modal
			 * layer, and it escapes the date popover's overflow:hidden.
			 */}
			<Popper
				open={timeOpen}
				anchorEl={timeAnchorEl}
				placement="bottom"
				transition
				modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
				sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
			>
				{({ TransitionProps }) => (
					<Grow {...TransitionProps} style={{ transformOrigin: "top center" }}>
						<MDBox
							sx={({ palette, borders, boxShadows }) => ({
								width: 168,
								borderRadius: borders.borderRadius.lg,
								border: `1px solid ${darkMode ? palette.grey[700] : palette.grey[300]}`,
								backgroundColor: darkMode ? palette.background.card : palette.white.main,
								boxShadow: boxShadows.lg,
								overflow: "hidden"
							})}
						>
							<ClickAwayListener onClickAway={handleTimeClickAway}>
								<MDBox display="flex" p={0.75}>
									{timeBound && (
										<>
											<TimeColumn
												key={`h-${timeField}`}
												label={i18n.t("FILTERS.panel.hour")}
												values={HOURS}
												selected={timeBound.getHours()}
												onSelect={hour => setBoundTime(timeField, { hour })}
												darkMode={darkMode}
											/>
											<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
											<TimeColumn
												key={`m-${timeField}`}
												label={i18n.t("FILTERS.panel.minute")}
												values={MINUTES}
												selected={timeBound.getMinutes()}
												onSelect={minute => setBoundTime(timeField, { minute })}
												darkMode={darkMode}
											/>
										</>
									)}
								</MDBox>
							</ClickAwayListener>
						</MDBox>
					</Grow>
				)}
			</Popper>
		</>
	);
}
